/**
 * Web Share API · Level 2 helper for MoodCard.
 *
 * iter-85 — the user wanted "share appropriate images spontaneously
 * without downloading." Web Share Level 2 lets us hand the OS a real
 * File (PNG/JPEG) + URL + caption in a single tap. The OS share sheet
 * then routes it to Instagram Story / WhatsApp / X / SMS / etc. with
 * the file attached, no manual download step.
 *
 * Browser support:
 *   ✓ iOS Safari 15+
 *   ✓ Android Chrome 75+ (with files since ~88)
 *   ✗ Desktop Safari (URL-only via navigator.share, no files)
 *   ✗ Desktop Firefox / older browsers — caller should fall back to a
 *      manual share sheet.
 *
 * Usage:
 *   const ok = await shareCardNative({ title, text, url, fileUrl });
 *   if (!ok) showManualSheet();
 */

async function fetchAsFile(fileUrl, suggestedName = "moodcard.png") {
    if (!fileUrl) return null;
    try {
        const res = await fetch(fileUrl, { credentials: "omit" });
        if (!res.ok) return null;
        const blob = await res.blob();
        // Honor the server-sent content-type; otherwise fall back to PNG.
        const type = blob.type || "image/png";
        const ext = type.includes("jpeg") ? "jpg" : type.includes("webp") ? "webp" : "png";
        const name = suggestedName.replace(/\.(png|jpe?g|webp)$/i, "") + "." + ext;
        return new File([blob], name, { type });
    } catch {
        return null;
    }
}

/**
 * Attempt native share. Returns true if the OS sheet was invoked,
 * false if the API isn't available or the user cancelled.
 *
 * Cancellation is intentionally treated as "false" so the caller can
 * decide whether to show a manual sheet — most apps DON'T (cancellation
 * was a deliberate action), but for MoodCard's "instant share" promise
 * we want at least the option.
 *
 * @param {Object}  opts
 * @param {string}  [opts.title]   — sheet title (some apps surface it).
 * @param {string}  [opts.text]    — caption / body text.
 * @param {string}  [opts.url]     — canonical share URL.
 * @param {string}  [opts.fileUrl] — absolute URL to a PNG/JPEG to attach.
 * @param {string}  [opts.fileName] — suggested filename (no path).
 * @returns {Promise<boolean>}
 */
export async function shareCardNative({ title, text, url, fileUrl, fileName } = {}) {
    if (typeof navigator === "undefined" || typeof navigator.share !== "function") {
        return false;
    }

    // Build the payload progressively. Some browsers reject if `files` is
    // present but unsupported, so we feature-detect via canShare().
    let files = null;
    if (fileUrl) {
        const file = await fetchAsFile(fileUrl, fileName || "moodcard.png");
        if (file) {
            const payloadWithFiles = { files: [file], title, text, url };
            // canShare may not exist on older versions; treat absence as "try it".
            if (typeof navigator.canShare === "function") {
                if (navigator.canShare(payloadWithFiles)) {
                    files = [file];
                }
            } else {
                files = [file];
            }
        }
    }

    const payload = files ? { files, title, text, url } : { title, text, url };
    try {
        await navigator.share(payload);
        return true;
    } catch (err) {
        // AbortError = user dismissed. NotAllowedError = security gate
        // (often when called outside a user gesture). Both → manual fallback.
        return false;
    }
}

/**
 * Lightweight detector for "can this device do native share at all?" Used
 * to decide whether to show "Share" vs "Copy link" in the UI.
 */
export function canNativeShare() {
    return typeof navigator !== "undefined" && typeof navigator.share === "function";
}
